import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// libs
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
// utils
import { cn } from '@repo/zolintra-utils';
// components
import {
  TextField, Skeleton, DropdownList, InputsRadioGroup,
} from '@repo/zolintra-ui';
import ReadPostActions from './ReadPostActions';
// hooks
import usePost from '../../../hooks/domain/usePost';
import useSearchParams from '../../../hooks/useSearchParams';
import useLeavePost from '../../../hooks/domain/useLeavePost';
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';
import { useAppContext } from '../../../store/AppContext';

// services
import { useLeavePostQuery } from '../../../services/leavePost/leavePostQueries';

import styles from './Form.module.css';

const dateOptions = [
  { value: 'morning', label: 'remoteWorkingAndLeave.readLeaveForm.date.morning' },
  { value: 'afternoon', label: 'remoteWorkingAndLeave.readLeaveForm.date.afternoon' },
];

function ReadLeaveForm({ postData }) {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const { getDateFromSlotsId, getDayTimeFromSlotId } = usePost();
  const { getSearchParams } = useSearchParams();
  const {
    year = new Date().getFullYear().toString(),
  } = getSearchParams();
  const { getTypesOptions } = useLeavePost();

  // Constants
  const startSlotId = Number(postData.includedStartSlotId);
  const endSlotId = Number(postData.excludedEndSlotId) - 1;
  const startDate = getDateFromSlotsId({ slotId: startSlotId, year });
  const endDate = getDateFromSlotsId({ slotId: endSlotId, year });
  const startDayTime = getDayTimeFromSlotId(startSlotId);
  const endDayTime = getDayTimeFromSlotId(endSlotId);

  // API Calls
  const { data: leavePost, isLoading } = useLeavePostQuery({ year, postId: postData.id });

  const typesOptions = getTypesOptions(true).map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const dayTimeOptions = useMemo(() => (
    dateOptions.map((option) => ({
      ...option,
      label: t(option.label),
    }))
  ), []);

  const [leaveType, setLeaveType] = useState(null);

  return (
    <>
      {isLoading ? (
        <>
          <div className="margin-bottom-m">
            <Skeleton shape="rectangle" width="50%" height="16px" className="margin-bottom-xs" />
            <Skeleton shape="rectangle" width="100%" height="56px" />
          </div>

          <div className={cn([styles['two-columns-fields'], 'margin-bottom-m'])}>
            <div>
              <Skeleton shape="rectangle" width="50%" height="16px" className="margin-bottom-xs" />
              <Skeleton shape="rectangle" width="100%" height="56px" />
            </div>

            <div className={cn([styles['two-columns-fields'], styles['align-bottom']])}>
              <Skeleton shape="rectangle" width="100%" height="42px" />
              <Skeleton shape="rectangle" width="100%" height="42px" />
            </div>
          </div>

          <div className={cn([styles['two-columns-fields'], 'margin-bottom-m'])}>
            <div>
              <Skeleton shape="rectangle" width="50%" height="16px" className="margin-bottom-xs" />
              <Skeleton shape="rectangle" width="100%" height="56px" />
            </div>

            <div className={cn([styles['two-columns-fields'], styles['align-bottom']])}>
              <Skeleton shape="rectangle" width="100%" height="42px" />
              <Skeleton shape="rectangle" width="100%" height="42px" />
            </div>
          </div>
        </>
      ) : (
        <>
          {leavePost?.type ? (
            <DropdownList
              value={typesOptions.find((option) => option.value === (leaveType || leavePost?.type))
                || null}
              label={t(
                'remoteWorkingAndLeave.readLeaveForm.type.label',
              )}
              options={typesOptions}
              noOptionsMessage={t('common.noOptionsMessage')}
              className="margin-bottom-m"
              isInsideDialog
              disabled={!hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)}
              onChange={(option) => setLeaveType(option.value)}
            />
          ) : null}

          <div className={cn([styles['two-columns-fields'], styles['align-bottom'], 'margin-bottom-m'])}>
            <TextField
              name="startDate"
              type="date"
              label={t('remoteWorkingAndLeave.readLeaveForm.date.from')}
              value={format(startDate, 'yyyy-MM-dd')}
              disabled
            />
            <InputsRadioGroup
              name="startDayTime"
              defaultValue={startDayTime}
              options={dayTimeOptions}
              disabled
            />
          </div>
          <div className={cn([styles['two-columns-fields'], styles['align-bottom'], 'margin-bottom-m'])}>
            <TextField
              name="endDate"
              type="date"
              label={t('remoteWorkingAndLeave.readLeaveForm.date.to')}
              value={format(endDate, 'yyyy-MM-dd')}
              disabled
            />
            <InputsRadioGroup
              name="endDayTime"
              defaultValue={endDayTime}
              options={dayTimeOptions}
              disabled
            />
          </div>

          {leavePost?.comment ? (
            <>
              <p className={styles['read-only-field-label']}>{t('remoteWorkingAndLeave.readLeaveForm.commentLabel')}</p>
              <div className={styles['read-only-field']}>
                {leavePost.comment}
              </div>
            </>
          ) : null}
        </>
      )}

      <ReadPostActions
        postData={{ ...postData }}
        postDetail={{ ...leavePost, type: leaveType }}
        year={year}
        isPostsListLoading={isLoading}
      />
    </>
  );
}

ReadLeaveForm.propTypes = {
  postData: PropTypes.shape({
    id: PropTypes.string,
    includedStartSlotId: PropTypes.number,
    excludedEndSlotId: PropTypes.number,
    status: PropTypes.oneOf(['Pending', 'Accepted', 'Refused']),
  }).isRequired,
};

ReadLeaveForm.defaultProps = {};

export default ReadLeaveForm;
